<template>
  <div class="row nav-padding">
    <div class="col-12">
      <div class="pull-left font-120">
        <strong>{{ dataCount }}</strong> {{
          dataCount > 1 ? 'Call dispositions' :
            'Call disposition'
        }}
      </div>
      <div class="pull-right">
        <call-disposition-creator ref="addCallDisposition"
                                  class="pull-left">
          <template v-slot:trigger>
            <button class="btn btn-md rounded greenish mb-2"
                    data-testid="add-call-disposition-button"
                    @click="$refs.addCallDisposition.hide_add = true">
              <i class="fa fa-plus"></i>
              Add Call Disposition
            </button>
          </template>
        </call-disposition-creator>
      </div>
    </div>
    <el-form
      ref="call_disposition_form"
      class="w-full"
      v-loading="$root.loading_call_disposition"
      :rules="rules_call_disposition"
      :model="editingCallDisposition"
      @submit.prevent.native="preventSubmit">
      <el-table
        :data="paginatedData"
        :default-sort="{prop: 'name', order: 'ascending'}"
        class="w-full"
        fit
        stripe>
        <el-table-column
          prop="id"
          label="ID"
          width="100"
          sortable
          data-testid="call-disposition-id-column">
          <template v-slot="scope">
            <span class="text-greyish">{{ scope.row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="is_external"
          sortable
          :min-width="120"
          data-testid="call-disposition-synced-column">
          <template v-slot:header>
            <span>Synced with CRM</span>
            <el-popover
              placement="right"
              width="500"
              trigger="hover">
              <div>
                <strong>Aloware Only</strong>
                <p>Call Dispositions are not synced with your CRM, and they are in Aloware only.</p>
                
                <strong>CRM & Aloware</strong>
                <p>Call Dispositions are in sync with your CRM.</p>
                
                <p>
                  You must manually sync the Call Dispositions for the new CRM Call Outcomes to appear in Aloware. To do that please go to Integrations > {Your CRM Settings} > Pull Call Outcomes
                </p>
                
                <p>
                  If you don’t manually sync, the new CRM Call Outcomes will appear once Aloware performs its daily sync at 6 PM PT.
                </p>
              </div>
              <i
                class="ml-1 fas fa-info-circle"
                slot="reference">
              </i>
            </el-popover>
          </template>
          
          <template v-slot="scope">
            {{ scope.row.is_external ? "Yes" : "No" }}
          </template>
        </el-table-column>
        <el-table-column
          prop="name"
          label="Name"
          :min-width="200"
          sortable
          data-testid="call-disposition-name-column">
          <template v-slot="scope">
            <div class="d-flex">
              <template>
                <i
                  v-if="(!editingCallDisposition || editingCallDisposition.id != scope.row.id) && scope.row.is_external"
                  class="material-icons"
                  :style="{ color: scope.row.color }">
                  lock
                </i>
                <i
                  v-else-if="(!editingCallDisposition || editingCallDisposition.id != scope.row.id) && !scope.row.is_external"
                  class="material-icons"
                  :style="{ color: scope.row.color }">
                  label
                </i>
                <el-color-picker
                  v-if="editingCallDisposition && editingCallDisposition.id == scope.row.id"
                  v-model="editingCallDisposition.color"
                  class="d-block"
                  @active-change="updateCallDispositionColor">
                </el-color-picker>
              </template>
              
              <span
                v-if="!editingCallDisposition || editingCallDisposition.id != scope.row.id || scope.row.is_external"
                class="text-greyish ml-2 align-self-center"
                :title="scope.row.name">
                                {{ scope.row.name }}
                            </span>
              <el-form-item
                v-else
                class="ml-2 flex-fill"
                prop="name">
                <el-input
                  v-model="editingCallDisposition.name"
                  placeholder="Name">
                </el-input>
              </el-form-item>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="description"
          label="Description"
          :min-width="200"
          sortable
          data-testid="call-disposition-description-column">
          <template v-slot="scope">
                        <span
                          v-if="!editingCallDisposition || editingCallDisposition.id != scope.row.id"
                          class="text-greyish">
                            {{ scope.row.description || '-' }}
                        </span>
            <el-form-item
              v-else
              prop="description">
              <el-input
                v-model="editingCallDisposition.description"
                placeholder="Description"
                type="textarea"
                rows="2"
                maxlength="190"
                show-word-limit>
              </el-input>
            </el-form-item>
          </template>
        </el-table-column>
        <el-table-column
          prop="contacts_count"
          label="Number Of Communications"
          :min-width="250"
          sortable
          data-testid="call-disposition-communications-column">
          <template v-slot="scope">
            <i
              v-if="!loaded_call_disposition.includes(scope.row.id)"
              class="fa fa-spin fa-spinner pull-left">
            </i>
            <button
              v-if="loaded_call_disposition.includes(scope.row.id)"
              class="btn b-bluish text-bluish btn-sm"
              data-testid="show-communications-button"
              @click="showCommunications(scope.row.id)">
              Show Communications
              <b class="label rounded danger m-l-xs">{{ scope.row.communications_count }}</b>
            </button>
          </template>
        </el-table-column>
        <el-table-column
          label="Operations"
          align="center"
          width="150"
          fixed="right"
          data-testid="call-disposition-operations-column">
          <template v-slot="scope">
            <div>
              <div v-if="!editingCallDisposition || editingCallDisposition.id != scope.row.id">
                <div
                  v-if="hasPermissionTo('update disposition status')"
                  class="row">
                  <div class="col-12">
                    <button
                      class="btn btn-sm btn-block info"
                      data-testid="edit-call-disposition-button"
                      @click="editCallDisposition(scope.row)">
                      <i class="fa fa-pencil pull-left"></i>
                      Edit
                    </button>
                  </div>
                </div>
                
                <div class="row mt-2"
                     v-if="hasPermissionTo('delete disposition status') && !scope.row.is_external">
                  <div class="col-12">
                    <button
                      class="btn btn-sm btn-block danger"
                      data-testid="delete-call-disposition-button"
                      @click="deleteCallDispositionRemote(scope.row.id)">
                      <i class="material-icons loader pull-left"
                         v-show="loading_delete_call_disposition == scope.row.id">&#xE863;</i>
                      <i class="fa fa-trash pull-left"
                         v-show="!loading_delete_call_disposition"></i>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="row">
                  <div class="col-12">
                    <button
                      class="btn btn-sm btn-block primary"
                      :disabled="loading_btn"
                      v-if="hasPermissionTo('update disposition status')"
                      data-testid="save-call-disposition-button"
                      @click="saveCallDisposition">
                      <i class="material-icons loader pull-left"
                         v-show="loading_btn">&#xE863;</i>
                      <i class="fa fa-save pull-left"
                         v-show="!loading_btn"></i>
                      Save
                    </button>
                  </div>
                </div>
                
                <div class="row mt-2">
                  <div class="col-12">
                    <button class="btn btn-sm btn-block danger"
                            data-testid="cancel-edit-call-disposition-button"
                            @click="cancelEditCallDisposition">
                      <i class="fa fa-close pull-left"></i>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
    <div class="mt-3"
         v-if="pageCount >= 1">
      <el-pagination
        class="pull-right"
        background
        layout="sizes, prev, pager, next"
        :page-sizes="[10, 20, 50, 100, 250]"
        :page-size="20"
        :current-page.sync="pagination.current_page"
        :total="dataCount"
        data-testid="call-disposition-pagination"
        @current-change="changePage"
        @size-change="handleSizeChange">
      </el-pagination>
      <div class="clearfix"></div>
    </div>
  </div>
</template>

<script>
import auth from '../auth'
import {mapActions, mapState} from 'vuex'
import {
  acl_mixin,
  paginator_mixin,
  form_validation_mixin
} from '../mixins'
import CallDispositionCreator from "../components/call-disposition-creator"

export default {
  mixins: [
    acl_mixin,
    paginator_mixin,
    form_validation_mixin
  ],
  
  components: {
    CallDispositionCreator
  },
  
  data() {
    return {
      reporting_url: localStorage.getItem('reporting_url'),
      auth: auth,
      company: null,
      loading_delete: false,
      loading: false,
      loading_btn: false,
      loading_delete_call_disposition: null,
      loaded_call_disposition: [],
      validated: false,
      editingCallDisposition: {
        id: null,
        color: null,
        name: null,
        description: null
      },
      dataKey: 'callDispositionsAlphabeticalOrder',
      rules_call_disposition: {
        name: [
          {
            required: true,
            message: 'Please provide a disposition status name',
            trigger: 'blur'
          },
          {
            max: 190,
            message: 'Name is too long',
            trigger: 'blur'
          }
        ],
        description: [
          {
            max: 190,
            message: 'Description is too long',
            trigger: 'blur'
          }
        ],
      }
    }
  },
  
  computed: {
    ...mapState(['call_dispositions', 'call_dispositions_loaded']),
    
    callDispositionsAlphabeticalOrder() {
      if (this.call_dispositions) {
        return this.call_dispositions.sort((a, b) => {
          let textA = a.name.toUpperCase()
          let textB = b.name.toUpperCase()
          return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
        })
      }
      
      return []
    },
  },
  
  created() {
    // in case event has already been dispatched, this will be the fallback
    if (this.call_dispositions_loaded) {
      this.getFullCallDisposition()
    }
    
    VueEvent.listen('call_disposition_loaded', () => {
      this.getFullCallDisposition()
    })
  },
  
  methods: {
    cancelEditCallDisposition() {
      this.editingCallDisposition = {
        id: null,
        color: null,
        name: null,
        description: null
      }
    },
    
    editCallDisposition(call_disposition) {
      this.editingCallDisposition = {
        id: call_disposition.id,
        color: call_disposition.color,
        name: call_disposition.name,
        description: call_disposition.description
      }
    },
    
    showCommunications(call_disposition_id) {
      this.$router.push({
        name: 'Dashboard',
        query: {
          call_disposition_id: call_disposition_id
        }
      }).catch(err => {
      })
    },
    
    getFullCallDisposition() {
      axios.get(this.reporting_url + '/api/v1/call-disposition-extended', {params: {extended_form: true}})
        .then(res => {
          res.data.forEach((call_disposition) => {
            this.call_dispositions.map((original_call_disposition) => {
              if (call_disposition.id == original_call_disposition.id) {
                this.loaded_call_disposition.push(original_call_disposition.id)
                original_call_disposition['communications_count'] = call_disposition.communications_count
              }
              return original_call_disposition
            })
          })
        })
        .catch(err => {
          console.log(err.response)
          this.$root.handleErrors(err.response)
        })
    },
    
    updateCallDispositionColor(val) {
      this.editingCallDisposition.color = val
    },
    
    saveCallDisposition() {
      if (!this.validateForm('call_disposition_form')) {
        this.preValidateForm('call_disposition_form')
        return
      }
      
      this.loading_btn = true
      axios.patch('/api/v1/call-disposition/' + this.editingCallDisposition.id, this.editingCallDisposition)
        .then(res => {
          this.loading_btn = false
          this.updateCallDisposition(res.data)
          this.$notify({
            offset: 95,
            title: 'Account',
            message: 'Call disposition has been successfully updated.',
            type: 'success',
            showClose: true,
          })
          this.cancelEditCallDisposition()
        })
        .catch(err => {
          this.$root.handleErrors(err.response)
          this.loading_btn = false
          this.cancelEditCallDisposition()
        })
    },
    
    deleteCallDispositionRemote: function (call_disposition_id) {
      if (this.call_dispositions) {
        let call_disposition = this.call_dispositions.find(call_disposition => call_disposition.id === call_disposition_id)
        this.$confirm('Deleting a call disposition will remove it from all communications. Continue?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          customClass: 'width-500 fixed',
          type: 'warning'
        }).then(() => {
          this.loading_delete_call_disposition = call_disposition_id
          axios.delete('/api/v1/call-disposition/' + call_disposition_id)
            .then(res => {
              this.deleteCallDisposition(call_disposition)
              this.$notify({
                offset: 95,
                title: 'Account',
                message: 'Call disposition has been deleted.',
                type: 'success',
                showClose: true,
              })
              this.loading_delete_call_disposition = null
            })
            .catch(err => {
              this.$root.handleErrors(err.response)
              this.loading_delete_call_disposition = null
            })
        }).catch(() => {
        
        })
      }
    },
    
    preventSubmit() {
      return
    },
    
    ...mapActions(['updateCallDisposition', 'deleteCallDisposition']),
  }
}
</script>
